<template>
	<div class="about">
		<h1>Hoe gebruik je de app?</h1>
		
		<section class="help-section">
			<h2>1. Inloggen</h2>
			<p>
				Om de app te gebruiken, moet je eerst inloggen met je lidnummer en geboortedatum.
			</p>
			<div class="image-placeholder">
				<img src="../assets/images/login.png" alt="Inlogscherm" />
			</div>
			<ol>
				<li>Voer je lidnummer in het eerste veld in</li>
				<li>Selecteer je geboortedatum met de datumkiezer</li>
				<li>Klik op de "Login" knop</li>
			</ol>
			<p>
				Na een succesvolle login word je doorgestuurd naar het hoofddashboard.
			</p>
		</section>

		<section class="help-section">
			<h2>2. Jouw unieke link delen</h2>
			<p>
				Direct na het inloggen zie je bovenaan het scherm jouw unieke link. Deze link is speciaal voor jou en ziet er zo uit:
			</p>
			<div class="code-block">
				https://de-geuzen.nl/webshop?lid=JOUW_LIDNUMMER
			</div>
			<div class="image-placeholder">
				<img src="../assets/images/uniekeLink.png" alt="link" />
			</div>
			<p>
				<strong>Belangrijk:</strong> Deze link kun je delen via WhatsApp, social media of andere kanalen. 
				Wanneer iemand via jouw link een bestelling plaatst, krijg jij daar verkooppunten voor!
			</p>
			<p>
				Tip: Kopieer de link en deel hem met vrienden, familie en kennissen om meer verkooppunten te verdienen.
			</p>
		</section>

		<section class="help-section">
			<h2>3. Een order plaatsen</h2>
			<p>
				Op het hoofddashboard zie je het formulier "Create Order". Hier kun je een nieuwe bestelling aanmaken voor een klant.
			</p>
			<div class="image-placeholder">
				<img src="../assets/images/createOrder.png" alt="Create Order" />
			</div>

			<h3>Stap 1: Klantgegevens invullen</h3>
			<ul>
				<li><strong>Bedrijf?</strong> Vink dit aan als de klant een bedrijf is en vul de bedrijfsnaam in</li>
				<li><strong>Voornaam</strong> en <strong>Achternaam</strong> van de klant (achternaam is verplicht)</li>
				<li><strong>Email</strong> (optioneel, maar aanbevolen)</li>
				<li><strong>Telefoon Nummer</strong> (optioneel)</li>
			</ul>

			<h3>Stap 2: Bezorgmethode kiezen</h3>
			<p>
				Kies tussen "Bezorgen" of "Ophalen":
			</p>

			<h4>Bij bezorgen:</h4>
			<ul>
				<li>Gebruik het zoekveld om een adres te vinden, of vul handmatig in:</li>
				<li>Straat, Huisnummer, Postcode en Stad</li>
				<li><strong>Let op:</strong> Wij leveren alleen in bepaalde postcodes. Als je een postcode invult die niet in ons bezorggebied ligt, krijg je een melding "Wij leveren niet op deze postcode"</li>
			</ul>

			<h4>Bij ophalen:</h4>
			<ul>
				<li>De klant haalt de bestelling op bij de Geuzen op Honkpad 1 in Alkmaar</li>
				<li>Je hoeft geen adresgegevens in te vullen</li>
			</ul>

			<h3>Stap 3: Aantal oliebolen selecteren</h3>
			<p>
				Vul in hoeveel zakken oliebolen de klant wil:
			</p>
			<ul>
				<li><strong>Aantal Met</strong> - oliebolen met krenten en rozijnen</li>
				<li><strong>Aantal Zonder</strong> - oliebolen zonder krenten en rozijnen</li>
			</ul>
			<p>
				Je moet minimaal één van beide aantallen invullen om een order te kunnen plaatsen.
			</p>

			<h3>Stap 4: Order aanmaken</h3>
			<p>
				Klik op de knop "Aanmaken" om de order te plaatsen. De order wordt aangemaakt en verschijnt in de orderoverzicht onderaan het scherm.
			</p>
		</section>

		<section class="help-section">
			<h2>4. QR code tonen aan klant</h2>
			<p>
				Nadat je een order hebt aangemaakt, verschijnt deze in het orderoverzicht onderaan het scherm. 
				Voor elke order kun je een QR code tonen door op de groene "QR" knop te klikken.
			</p>
			<div class="image-placeholder">
				<img src="../assets/images/qrCodeButton.png" alt="QR Code Button" />
			</div>
			<p>
				Wanneer je op de QR knop klikt, wordt er een grote QR code getoond. Deze QR code bevat een link naar de betaalpagina op de website.
			</p>
			<div class="image-placeholder">
				<img src="../assets/images/qrCode.png" alt="QR Code" />
			</div>
			<p>
				<strong>Belangrijk:</strong> 
			</p>
			<ul>
				<li>De klant moet de QR code scannen met zijn of haar telefoon</li>
				<li>Dit leidt de klant naar de website waar hij of zij online kan afrekenen</li>
				<li><strong>Contant afrekenen is niet mogelijk</strong> - alle betalingen moeten online via de website worden gedaan</li>
				<li>Zorg ervoor dat de klant de QR code goed kan scannen (voldoende licht, geen reflectie)</li>
			</ul>
			<p>
				Om de QR code weer te sluiten, klik je op de rode "QR Sluiten" knop.
			</p>
		</section>

		<section class="help-section">
			<h2>5. Belangrijke informatie</h2>
			
			<h3>Postcodes</h3>
			<p>
				<strong>Wij leveren alleen in bepaalde postcodes.</strong> Als je een postcode invult die niet in ons bezorggebied ligt, 
				kun je de order niet aanmaken. Controleer altijd of de postcode geldig is voordat je alle gegevens invult.
			</p>

			<h3>Betaling</h3>
			<p>
				<strong>Contant afrekenen kan niet.</strong> Alle klanten moeten online betalen via de website. 
				De QR code die je toont leidt de klant naar de betaalpagina waar hij of zij met iDEAL, creditcard of andere online betaalmethoden kan betalen.
			</p>

			<h3>Verkooppunten</h3>
			<p>
				Wanneer je jouw unieke link deelt en iemand gebruikt die link om een bestelling te plaatsen op de website, 
				krijg jij daar verkooppunten voor. Dit is een manier om je verkoop te stimuleren!
			</p>

			<h3>Orderoverzicht</h3>
			<p>
				In het orderoverzicht zie je alle orders die je hebt aangemaakt. Je kunt hier zien:
			</p>
			<ul>
				<li>Naar wie je hebt verkocht (naam en adres)</li>
				<li>De status van de order</li>
				<li>Hoeveel zakken er zijn verkocht</li>
				<li>De QR code om te tonen aan de klant</li>
			</ul>
			<p>
				Je kunt het overzicht verversen door op de "Herladen" knop te klikken.
			</p>
		</section>

		<section class="help-section">
			<h2>Problemen?</h2>
			<p>
				Indien iets niet werkt of er gaat iets niet goed, gebruik dan het papieren formulier en laat dit weten aan een van de leiding.
			</p>
		</section>
	</div>
</template>

<style scoped>
.about {
	max-width: 900px;
	margin: 0 auto;
	padding: 20px;
	text-align: left;
}

.help-section {
	margin-bottom: 40px;
}

.help-section h2 {
	color: #2c3e50;
	border-bottom: 2px solid #42b983;
	padding-bottom: 10px;
	margin-top: 30px;
}

.help-section h3 {
	color: #2c3e50;
	margin-top: 20px;
	margin-bottom: 10px;
}

.help-section h4 {
	color: #555;
	margin-top: 15px;
	margin-bottom: 8px;
}

.help-section ul,
.help-section ol {
	margin-left: 20px;
	margin-bottom: 15px;
}

.help-section li {
	margin-bottom: 8px;
}

.code-block {
	background-color: #f4f4f4;
	border: 1px solid #ddd;
	border-radius: 4px;
	padding: 10px;
	font-family: monospace;
	margin: 15px 0;
	word-break: break-all;
}

.image-placeholder {
	margin: 20px 0;
	text-align: center;
	display: flex;
	align-items: center;
	justify-content: center;
	/* Default placeholder styling */
	background-color: #f9f9f9;
	border: 2px dashed #ccc;
	border-radius: 8px;
	padding: 40px 20px;
	color: #999;
	font-style: italic;
	min-height: 200px;
}

/* When image is present, remove placeholder styling */
.image-placeholder img {
	max-width: 100%;
	height: auto;
	width: auto;
	border-radius: 8px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
	display: block;
}

/* Hide placeholder border/background when image exists */
.image-placeholder:has(img) {
	background-color: transparent;
	border: none;
	padding: 10px;
	min-height: auto;
}

/* Show camera icon only when no image */
.image-placeholder:not(:has(img))::before {
	content: "📷";
	font-size: 48px;
	display: block;
	margin-bottom: 10px;
}

/* Responsive adjustments for images */
@media (max-width: 767px) {
	.image-placeholder {
		padding: 15px 10px;
	}
	
	.image-placeholder img {
		max-width: 100%;
	}
	
	.image-placeholder:has(img) {
		padding: 10px 5px;
	}
}

@media (min-width: 768px) {
	.image-placeholder img {
		max-width: 90%;
	}
}

@media (min-width: 1024px) {
	.image-placeholder img {
		max-width: 85%;
	}
}
</style>
